import { supabase } from '../lib/supabase';

/**
 * Universal user object interface that works for both OAuth and phone users
 */
export interface UniversalUser {
  id: string;
  email?: string;
  phone?: string;
  provider: 'oauth' | 'phone';
  firebase_uid?: string;
}

/**
 * Get current user - works for BOTH OAuth and phone authenticated users
 * @returns Universal user object or null
 */
export async function getCurrentUser(): Promise<UniversalUser | null> {
  try {
    // Prefer getSession() (reads local storage, no network round-trip) to avoid
    // false "not authenticated" errors during session hydration timing windows.
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      return {
        id: session.user.id,
        email: session.user.email,
        phone: session.user.phone,
        provider: 'oauth',
      };
    }

    // Fallback: network call to Supabase to verify JWT
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      return {
        id: user.id,
        email: user.email,
        phone: user.phone,
        provider: 'oauth',
      };
    }

    // STEP 2: Check for phone auth session
    const phoneAuthSession = localStorage.getItem('phone_auth_session');
    if (phoneAuthSession) {
      try {
        const phoneSession = JSON.parse(phoneAuthSession);
        return {
          id: phoneSession.user_id,
          email: phoneSession.email || undefined,
          phone: phoneSession.phone,
          provider: 'phone',
          firebase_uid: phoneSession.firebase_uid,
        };
      } catch (e) {
        console.error('Error parsing phone session:', e);
      }
    }

    return null;
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    return null;
  }
}

/**
 * Get current user ID - works for BOTH OAuth and phone authenticated users
 * @returns User ID string or null
 */
export async function getCurrentUserId(): Promise<string | null> {
  try {
    const user = await getCurrentUser();
    return user?.id || null;
  } catch (error) {
    console.error('Error in getCurrentUserId:', error);
    return null;
  }
}

export const extractSessionTokensFromUrlHash = (): { accessToken: string; refreshToken: string; expiresIn: number; tokenType: string; } | null => {
  if (typeof window === 'undefined' || !window.location.hash) {
    return null;
  }

  const hash = window.location.hash.substring(1); // Remove the '#'
  const params = new URLSearchParams(hash);

  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  const expiresIn = params.get('expires_in');
  const tokenType = params.get('token_type');
  const type = params.get('type'); // Check for 'recovery' type

  if (accessToken && refreshToken && expiresIn && tokenType && type === 'recovery') {
    return {
      accessToken,
      refreshToken,
      expiresIn: parseInt(expiresIn, 10),
      tokenType,
    };
  }

  return null;
};

/**
 * Validates phone number format
 * @param phone Phone number to validate
 * @returns Boolean indicating if phone number is valid
 */
export const validatePhoneNumber = (phone: string): boolean => {
  // Remove all spaces and non-digit characters except +
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  
  // Check if it starts with + and has 10-15 digits
  const phoneRegex = /^\+[1-9]\d{9,14}$/;
  return phoneRegex.test(cleanPhone);
};

/**
 * Formats phone number for display
 * @param phone Phone number to format
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters except +
  const cleanPhone = phone.replace(/[^\d+]/g, '');
  
  // Basic formatting for common patterns
  if (cleanPhone.startsWith('+1') && cleanPhone.length === 12) {
    // US format: +1 (XXX) XXX-XXXX
    return `+1 (${cleanPhone.slice(2, 5)}) ${cleanPhone.slice(5, 8)}-${cleanPhone.slice(8)}`;
  } else if (cleanPhone.startsWith('+91') && cleanPhone.length === 13) {
    // India format: +91 XXXXX XXXXX
    return `+91 ${cleanPhone.slice(3, 8)} ${cleanPhone.slice(8)}`;
  }
  
  // Return as-is if no specific formatting applies
  return cleanPhone;
};
